<template>
  <div class="space-y-6">
    <!-- 页面标题 -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-700">余额变动记录</h2>
      <div class="flex items-center mt-2">
        <router-link to="/admin/dashboard" class="text-gray-600 hover:text-gray-800">首页</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <router-link to="/admin/players" class="text-gray-600 hover:text-gray-800">玩家管理</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">余额变动记录</span>
      </div>
    </div>
    
    <!-- 统计卡片 -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <i class="fas fa-chart-line text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">今日交易笔数</p>
            <p class="text-2xl font-semibold text-gray-800">{{ formatNumber(stats.todayTotalCount) }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">较昨日</p>
            <p :class="['text-sm font-semibold', stats.percentChange >= 0 ? 'text-green-600' : 'text-red-600']">
              {{ stats.percentChange >= 0 ? '+' : '' }}{{ stats.percentChange.toFixed(2) }}%
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <i class="fas fa-arrow-up text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">收入交易</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.incomeAmount }} {{ stats.currency }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">交易数</p>
            <p class="text-sm text-blue-600 font-semibold">
              {{ formatNumber(stats.incomeCount) }} ({{ stats.incomePercentage.toFixed(2) }}%)
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-red-100 text-red-600">
            <i class="fas fa-arrow-down text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">支出交易</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.expenseAmount }} {{ stats.currency }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">交易数</p>
            <p class="text-sm text-blue-600 font-semibold">
              {{ formatNumber(stats.expenseCount) }} ({{ stats.expensePercentage.toFixed(2) }}%)
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-purple-100 text-purple-600">
            <i class="fas fa-users text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">交易用户数</p>
            <p class="text-2xl font-semibold text-gray-800">{{ formatNumber(stats.uniqueUsers) }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">人均交易</p>
            <p class="text-sm text-purple-600 font-semibold">{{ stats.avgTransactions.toFixed(2) }}</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 筛选器 -->
    <SearchFilters
      :filter-config="filterConfig"
      :initial-filters="filters"
      @search="search"
      @reset="resetFilters"
    />
    
    <!-- 余额变动记录表格 -->
    <div class="bg-white shadow rounded-md overflow-hidden">
      <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
        <h3 class="text-lg leading-6 font-medium text-gray-900">余额变动记录</h3>
        <div class="flex space-x-3">
          <button @click="exportData" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <i class="fas fa-download mr-2"></i> 导出数据
          </button>
        </div>
      </div>
      
      <TableList
        :columns="columns"
        :items="walletLogs"
        :loading="loading"
        :total-items="totalItems"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        empty-text="暂无余额变动记录"
        @page-change="handlePageChange"
      >
        <!-- 金额 -->
        <template #cell-amount="{ item }">
          <span :class="['text-sm font-medium', parseFloat(item.amount) >= 0 ? 'text-green-600' : 'text-red-600']">
            {{ parseFloat(item.amount) >= 0 ? '+' : '' }}{{ formatNumber(item.amount, 2) }}
          </span>
        </template>
        
        <!-- 交易前余额 -->
        <template #cell-before_balance="{ item }">
          <span class="text-sm text-gray-900">{{ formatNumber(item.before_balance, 2) }}</span>
        </template>
        
        <!-- 交易后余额 -->
        <template #cell-after_balance="{ item }">
          <span class="text-sm text-gray-900">{{ formatNumber(item.after_balance, 2) }}</span>
        </template>
        
        <!-- 游戏 -->
        <template #cell-game="{ item }">
          <span class="text-sm text-gray-900">{{ item.game || '-' }}</span>
        </template>
      </TableList>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import SearchFilters from '@/components/common/SearchFilters.vue';
import TableList from '@/components/common/TableList.vue';
import AdminApiClient from '@/utils/admin-api-client';

const apiClient = new AdminApiClient();

// 状态变量
const loading = ref(false);
const walletLogs = ref([]);
const totalItems = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(15);

// 统计数据
const stats = reactive({
  todayTotalCount: 0,
  percentChange: 0,
  incomeAmount: '0.00',
  incomeCount: 0,
  incomePercentage: 0,
  expenseAmount: '0.00',
  expenseCount: 0,
  expensePercentage: 0,
  uniqueUsers: 0,
  avgTransactions: 0,
  currency: 'HKD'
});

// 筛选条件
const filters = reactive({
  id: '',
  username: '',
  user_id: '',
  game: '',
  currency: '',
  created_at: '',
  mer_id: ''
});

// 表格列配置
const columns = [
  { key: 'id', label: '交易ID' },
  { key: 'username', label: '用户名' },
  { key: 'user_id', label: '用户ID' },
  { key: 'amount', label: '金额' },
  { key: 'before_balance', label: '交易前余额' },
  { key: 'after_balance', label: '交易后余额' },
  { key: 'currency', label: '货币' },
  { key: 'game', label: '游戏' },
  { key: 'mer_id', label: '商户ID' },
  { key: 'created_at', label: '交易时间' }
];

// 筛选器配置
const filterConfig = reactive([
  {
    id: 'id',
    type: 'input',
    label: '交易ID',
    placeholder: '输入交易ID',
    icon: 'fas fa-hashtag'
  },
  {
    id: 'username',
    type: 'input',
    label: '用户名',
    placeholder: '输入用户名',
    icon: 'fas fa-user'
  },
  {
    id: 'user_id',
    type: 'input',
    label: '用户ID',
    placeholder: '输入用户ID',
    icon: 'fas fa-id-card'
  },
  {
    id: 'game',
    type: 'input',
    label: '游戏',
    placeholder: '输入游戏名称',
    icon: 'fas fa-gamepad'
  },
  {
    id: 'currency',
    type: 'select',
    label: '货币',
    placeholder: '选择货币',
    icon: 'fas fa-dollar-sign',
    options: [
      { value: '', label: '全部货币' },
      { value: 'HKD', label: 'HKD' },
      { value: 'USD', label: 'USD' },
      { value: 'CNY', label: 'CNY' }
    ]
  },
  {
    id: 'created_at',
    type: 'date',
    label: '交易日期',
    placeholder: '选择日期',
    icon: 'fas fa-calendar'
  },
  {
    id: 'mer_id',
    type: 'input',
    label: '商户ID',
    placeholder: '输入商户ID',
    icon: 'fas fa-building'
  }
]);

// 搜索方法
function search(newFilters) {
  Object.assign(filters, newFilters);
  currentPage.value = 1;
  loadWalletLogs();
}

// 重置筛选条件
function resetFilters() {
  // 清空所有筛选条件
  Object.keys(filters).forEach(key => {
    filters[key] = '';
  });
  
  // 清除localStorage中的缓存
  clearLocalStorageCache();
  
  // 重置页码并重新加载数据
  currentPage.value = 1;
  loadWalletLogs();
}

// 清除localStorage缓存
function clearLocalStorageCache() {
  // 清除玩家筛选相关的所有缓存
  localStorage.removeItem('filter_player_username');
  localStorage.removeItem('filter_player_id');
  
  // 可以根据需要清除其他相关缓存
  console.log('已清除玩家筛选缓存');
}

// 加载余额变动记录
function loadWalletLogs(page = 1) {
  loading.value = true;
  currentPage.value = page;

  apiClient.getWalletLogs(filters, page, itemsPerPage.value)
    .then(response => {
      const data = response.data || {};
      walletLogs.value = data.logs || [];
      totalItems.value = data.total || 0;
      loading.value = false;
      
      console.log(`加载余额变动记录: 第${page}页, 共${data.total}条记录`);
    })
    .catch(error => {
      console.error('加载余额变动记录失败:', error);
      loading.value = false;
    });
}

// 加载统计数据
function loadWalletStats() {
  apiClient.getWalletStats()
    .then(response => {
      const data = response.data || {};
      
      // 设置统计数据
      const todayStats = data.todayStats || {};
      const yesterdayStats = data.yesterdayStats || {};
      
      stats.todayTotalCount = todayStats.total_count || 0;
      stats.incomeAmount = formatNumber(todayStats.income_amount || 0, 2);
      stats.incomeCount = todayStats.income_count || 0;
      stats.incomePercentage = todayStats.income_percentage || 0;
      stats.expenseAmount = formatNumber(todayStats.expense_amount || 0, 2);
      stats.expenseCount = todayStats.expense_count || 0;
      stats.expensePercentage = todayStats.expense_percentage || 0;
      stats.uniqueUsers = todayStats.unique_users || 0;
      stats.avgTransactions = todayStats.avg_transactions || 0;
      stats.currency = todayStats.currency || 'HKD';
      
      // 计算变化百分比
      const yesterdayCount = yesterdayStats.total_count || 0;
      if (yesterdayCount > 0) {
        stats.percentChange = Number((((stats.todayTotalCount - yesterdayCount) / yesterdayCount) * 100).toFixed(1));
      } else {
        stats.percentChange = stats.todayTotalCount > 0 ? 100 : 0;
      }
    })
    .catch(error => {
      console.error('加载统计数据失败:', error);
    });
}

// 导出数据
function exportData() {
  // 构建导出URL
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.append(key, filters[key]);
    }
  });
  
  const exportUrl = `/admin/api/wallet/export-logs?${params.toString()}`;
  window.open(exportUrl, '_blank');
}

// 处理页码变化
function handlePageChange(page) {
  console.log(`切换到第${page}页`);
  loadWalletLogs(page);
}

// 格式化数字
function formatNumber(number, fractionDigits = 0) {
  return Number(number).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
}

// 页面加载时初始化
onMounted(() => {
  loadWalletStats();
  loadWalletLogs();
});
</script> 